import React, { useState, useMemo, useEffect, useLayoutEffect } from 'react'
import FlowerImg from '../../../../assets/images/signup/flower.png'
import {
  getEncodedDataFromUrl,
  isInApp,
} from '../../../../utils/helpers/common'
import {
  clearSemaOnboardingPersisted,
  getSemaOnboardingStepPersisted,
  getSemaAskDoctorPersisted,
  setSemaAskDoctorPersisted,
  getSemaSkipAddressPersisted,
  setSemaSkipAddressPersisted,
  setSemaOnboardingStepPersisted,
} from '../../../../utils/helpers/semaOnboardingStorage'
import SignupPageHeader from '../../../SignupUser/SignupPageHeader'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import {
  DOCTOR_DETAILS_STEP,
  SEMA_ONBOARDING_STEPS,
  SEMA_PATIENT_ADDRESS_STEP,
  SEMA_UPLOAD_DOCUMENTS_STEP,
} from '../../../../constants/sema-constants'
import postMessageToRN, {
  GO_TO_APP,
} from '../../../../utils/helpers/ReactNativeEvents'
import * as actions from '../../../../store/slices'
import { EVENTS } from '../../../../analytics/events'
import { trackEvent } from '../../../../analytics'
// Default onboarding steps for CGM programs
// TODO: Need to update
const OnboardingPage = ({ onFinish, loading }) => {
  const params = getEncodedDataFromUrl()
  const { variant } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { updatePatientInfoState } = useSelector((s) => s.auth)
  const { userDetailsState } = useSelector((s) => s.authentication)
  const patientDetails = userDetailsState?.data || {}

  // console.log('params', params)
  const [formFields, setFormFields] = useState({
    name: '',
    state: '',
    city: '',
    gender: '',
    dob: '',
  })

  const submitting = updatePatientInfoState?.loading
  const [currentStep, setCurrentStep] = useState(0)

  // URL ?data=... can be missing after app kill/reopen; persist 3-step intent + step
  const useThreeStepFlow = useMemo(() => {
    if (params?.askDoctorDetails) return true
    if (!variant) return false
    return getSemaAskDoctorPersisted(variant)
  }, [params?.askDoctorDetails, variant])

  useEffect(() => {
    if (params?.askDoctorDetails && variant) {
      setSemaAskDoctorPersisted(variant)
    }
  }, [params?.askDoctorDetails, variant])

  // Address is asked unless the backend says the patient already has one — a renewal, where
  // re-collecting it is pointless friction. Persisted for the same reason as askDoctor: the
  // `?data=` param is lost if the app is killed mid-flow. An absent param means ask, so
  // first-time onboarding is unchanged.
  const skipAddress = useMemo(() => {
    if (params?.askAddress === false) return true
    if (!variant) return false
    return getSemaSkipAddressPersisted(variant)
  }, [params?.askAddress, variant])

  useEffect(() => {
    if (params?.askAddress === false && variant) {
      setSemaSkipAddressPersisted(variant)
    }
  }, [params?.askAddress, variant])

  useLayoutEffect(() => {
    if (!variant) return
    if (useThreeStepFlow) {
      const raw = getSemaOnboardingStepPersisted(variant)
      const maxIdx = 2
      setCurrentStep(Math.min(maxIdx, Math.max(0, raw)))
    } else {
      setCurrentStep(0)
    }
  }, [useThreeStepFlow, variant])

  useEffect(() => {
    trackEvent(EVENTS.LANDING_ON_ONBOARDING_SCREEN, {
      screenName: 'Onboarding Screen',
      medicine: variant,
    })
  }, [variant])

  const handleChange = (field, value) => {
    setFormFields({ ...formFields, [field]: value })
  }

  const onboardingSteps = useMemo(() => {
    if (useThreeStepFlow) {
      // 1: doctor, 2: user address, 3: document upload
      return [
        DOCTOR_DETAILS_STEP,
        ...(skipAddress ? [] : [SEMA_PATIENT_ADDRESS_STEP]),
        SEMA_UPLOAD_DOCUMENTS_STEP,
      ]
    }
    if (skipAddress) {
      // Renewal: the patient already has a doctor and an address, so uploading the
      // document is the only thing left to do.
      return SEMA_ONBOARDING_STEPS.filter(
        (step) => step?.name !== SEMA_PATIENT_ADDRESS_STEP?.name,
      )
    }
    return SEMA_ONBOARDING_STEPS
  }, [useThreeStepFlow, skipAddress])

  const stepIndex = Math.min(
    currentStep,
    Math.max(0, (onboardingSteps?.length || 1) - 1),
  )

  const activeStep = useMemo(() => {
    return onboardingSteps[stepIndex]
  }, [stepIndex, onboardingSteps])

  const handleFinish = () => {
    if (variant) {
      clearSemaOnboardingPersisted(variant)
    }
    postMessageToRN(GO_TO_APP)
  }

  const rightImage = location.state?.programLogo || ''

  const handleNextStep = (flag = false, payload = null) => {
    // console.log('flag', flag)
    if (flag && payload) {
      setFormFields((prev) => ({ ...prev, doctorStepSelection: payload }))
      const finalPayload = {
        patientId: patientDetails?.patientID,
        newDoctorId:
          payload?.selectedDoctor?.id ||
          payload?.selectedDoctor?.doctor_id ||
          payload?.selectedDoctor?.dr_id,
        newDoctorClinicId:
          payload?.selectedHospital?.clinic_master_id ||
          payload?.selectedHospital?.clinic_id,
        newDoctorClinicAddress:
          payload?.selectedHospital?.address ||
          payload?.selectedHospital?.clinic_address ||
          payload?.selectedHospital?.clinic_name,
      }
      dispatch(actions.saveDoctorDetailsRequest(finalPayload))
    }
    if (payload?.stepType === 'address') {
      setFormFields((prev) => ({
        ...prev,
        addressStepSelection: {
          selectedState: payload?.selectedState,
          selectedCity: payload?.selectedCity,
        },
        addressStepFormFields: payload?.formFields || {},
      }))
    }
    const lastIdx = (onboardingSteps?.length || 1) - 1
    if (stepIndex !== lastIdx) {
      const next = stepIndex + 1
      setCurrentStep(next)
      if (useThreeStepFlow && variant) {
        setSemaOnboardingStepPersisted(variant, next)
      }
    } else {
      if (!loading) {
        handleFinish()
      }
    }
  }

  const handlePreviousStep = () => {
    if (submitting) return
    if (stepIndex > 0) {
      const prev = stepIndex - 1
      setCurrentStep(prev)
      if (useThreeStepFlow && variant) {
        setSemaOnboardingStepPersisted(variant, prev)
      }
      return
    } else {
      if (isInApp()) {
        postMessageToRN(GO_TO_APP)
      } else {
        navigate(`/${variant}`)
      }
    }
  }

  const progress = useMemo(() => {
    return ((stepIndex + 1) / onboardingSteps?.length) * 100
  }, [stepIndex, onboardingSteps])

  return (
    <div className="min-w-sm max-w-lg pb-[98px] mx-auto w-full h-screen overflow-y-auto">
      <SignupPageHeader
        handlePreviousStep={handlePreviousStep}
        progress={progress}
        step={stepIndex}
        totalSteps={onboardingSteps?.length}
        rightImage={rightImage}
        themeColor="#C14FA5"
        containerClassName="!pt-0"
      />
      <div className="relative">
        {activeStep?.hideImage ? null : (
          <img
            src={FlowerImg}
            alt="flower"
            className="w-[160px] h-[160px] absolute -z-10 top-0 right-0 -mr-[2px]"
          />
        )}
        {activeStep?.Component ? (
          <div className={classNames('pb-8', activeStep?.bg)}>
            <activeStep.Component
              name={activeStep?.name}
              value={formFields[activeStep?.name]}
              formObj={formFields}
              initialSelection={
                activeStep?.name === 'doctorDetails'
                  ? formFields?.doctorStepSelection
                  : activeStep?.name === 'address'
                    ? formFields?.addressStepSelection
                    : undefined
              }
              initialFormFields={
                activeStep?.name === 'address'
                  ? formFields?.addressStepFormFields
                  : undefined
              }
              onChange={handleChange}
              onMoveNext={handleNextStep}
              onSkip={handleNextStep}
              loading={submitting}
              isLastStep={stepIndex === onboardingSteps?.length - 1}
              pageObj={activeStep}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default OnboardingPage
